import { Info } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { DayToggleGrid } from "../../../../../components/UIToolkit/Clickable/DayToggleGrid";
import { DAYS_IN_WEEK, isWeekdayCode, WEEKDAY_CODES, type WeekdayCode } from "./autoApproveConsts";

type Props = { shiftDays: string[]; onChange: (shiftDays: WeekdayCode[]) => void; dataTestId: string };

/** The week as seven day cells, Monday first. A rule stores every day as `[]`, so that state lights all seven. */
export const AutoApproveRuleDays: FC<Props> = ({ shiftDays, onChange, dataTestId }) => {
	const { i18n } = useTranslation();
	const days = shiftDays.filter(isWeekdayCode);
	const lit: readonly WeekdayCode[] = days.length === 0 ? WEEKDAY_CODES : days;
	// Luxon's weekday lists start on Monday, the same order as `WEEKDAY_CODES`.
	const shortNames = Info.weekdays("short", { locale: i18n.language });
	const longNames = Info.weekdays("long", { locale: i18n.language });

	const toggle = (code: WeekdayCode) => {
		const next = WEEKDAY_CODES.filter((day) => (day === code) !== lit.includes(day));
		// All seven and none both mean every day, which a rule stores as `[]`.
		onChange(next.length === DAYS_IN_WEEK ? [] : next);
	};

	return (
		<DayToggleGrid
			stretch
			size="medium"
			look="outlined"
			dataTestId={dataTestId}
			groups={[
				{
					key: "week",
					cells: WEEKDAY_CODES.map((code, index) => ({
						key: code,
						label: shortNames[index],
						ariaLabel: longNames[index],
						selected: lit.includes(code),
						dataTestId: `${dataTestId}-${code}`,
					})),
				},
			]}
			onToggle={(cell) => {
				const code = String(cell.key);
				if (!isWeekdayCode(code)) throw new Error(`Unknown weekday cell: ${code}`);
				toggle(code);
			}}
		/>
	);
};
